import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import Cropper from "react-easy-crop";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Plus, Pencil, Trash2, Upload, X, ArrowLeft } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import PageHeader from "../../components/admin/PageHeader";
import InfoBanner from "../../components/admin/InfoBanner";

const BASE_URI = process.env.REACT_APP_API_BASE_URI;

export const BannerCrud = () => {
  const [banners, setBanners] = useState([]);
  const [newBanner, setNewBanner] = useState({ images: [], imagePreviews: [] });
  const [editBanner, setEditBanner] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({ page: 1, page_size: 10, total: 0, total_pages: 1 });

  // Crop states
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropImageUrl, setCropImageUrl] = useState("");
  const [replacingImageIndex, setReplacingImageIndex] = useState(-1);
  const [pendingFiles, setPendingFiles] = useState([]);

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  useEffect(() => { fetchBanners(); }, []);

  const compressImage = async (blob, quality = 0.9) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width; canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      canvas.toBlob(resolve, "image/jpeg", quality);
    };
    img.src = URL.createObjectURL(blob);
  });

  const openCropForFile = (file, replacingIdx = -1) => {
    setCropImageUrl(URL.createObjectURL(file));
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setReplacingImageIndex(replacingIdx);
    setCropModalOpen(true);
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const valid = files.filter((f) => {
      if (!f.type.startsWith("image/")) { toast.warning(`${f.name} is not an image`); return false; }
      if (f.size > 5 * 1024 * 1024) { toast.warning(`${f.name} exceeds 5MB`); return false; }
      return true;
    });
    if (valid.length === 0) return;
    const [first, ...rest] = valid;
    setPendingFiles(rest);
    openCropForFile(first);
  };

  const handleReplaceImage = (e, idx) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.warning("Not an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.warning("File exceeds 5MB"); return; }
    openCropForFile(file, idx);
  };

  const onCropComplete = useCallback((_, pixels) => { setCroppedAreaPixels(pixels); }, []);

  const cancelCrop = () => {
    setCropModalOpen(false);
    setCropImageUrl("");
    setReplacingImageIndex(-1);
    setPendingFiles([]);
  };

  const saveCroppedImage = async () => {
    if (!croppedAreaPixels) { toast.warning("Please wait for the crop area to load"); return; }
    try {
      const canvas = document.createElement("canvas");
      const img = new Image();
      await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = cropImageUrl; });
      canvas.width = croppedAreaPixels.width; canvas.height = croppedAreaPixels.height;
      canvas.getContext("2d").drawImage(img, croppedAreaPixels.x, croppedAreaPixels.y, croppedAreaPixels.width, croppedAreaPixels.height, 0, 0, croppedAreaPixels.width, croppedAreaPixels.height);
      let blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.9));
      blob = await compressImage(blob);
      const file = new File([blob], "cropped-banner.jpg", { type: "image/jpeg" });
      const preview = URL.createObjectURL(file);

      if (editBanner && replacingImageIndex >= 0) {
        const imgs = [...editBanner.images], prevs = [...(editBanner.imagePreviews || [])];
        if (prevs[replacingImageIndex]) URL.revokeObjectURL(prevs[replacingImageIndex]);
        imgs[replacingImageIndex] = file; prevs[replacingImageIndex] = preview;
        setEditBanner({ ...editBanner, images: imgs, imagePreviews: prevs });
      } else if (editBanner) {
        setEditBanner({ ...editBanner, images: [...editBanner.images, file], imagePreviews: [...(editBanner.imagePreviews || []), preview] });
      } else {
        setNewBanner((prev) => ({ images: [...prev.images, file], imagePreviews: [...prev.imagePreviews, preview] }));
      }

      // Process next queued file or close crop view
      if (pendingFiles.length > 0) {
        const [next, ...rest] = pendingFiles;
        setPendingFiles(rest);
        openCropForFile(next);
      } else {
        setCropModalOpen(false);
        setCropImageUrl("");
        setReplacingImageIndex(-1);
      }
    } catch { toast.error("Failed to crop image"); }
  };

  const fetchBanners = async (page = 1) => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URI}/entities?category=banner&page=${page}&limit=${pagination.page_size}`, getAuthHeaders());
      if (res.data?.data) {
        setBanners(res.data.data.map((i) => ({ id: i.id, images: i.data.images || [] })));
        setPagination({ page: res.data.page, page_size: res.data.limit, total: res.data.total, total_pages: Math.ceil(res.data.total / res.data.limit) });
      }
      setError("");
    } catch { setError("Failed to fetch banners"); }
    finally { setLoading(false); }
  };

  const createBanner = async (e) => {
    e.preventDefault();
    if (newBanner.images.length === 0) { toast.warning("Please add at least one image"); return; }
    try {
      setLoading(true);
      const fd = new FormData();
      newBanner.images.forEach((f) => fd.append("images", f));
      await axios.post(`${BASE_URI}/entities?category=banner`, fd, { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${localStorage.getItem("token")}` } });
      toast.success("Banner created!");
      newBanner.imagePreviews.forEach((u) => URL.revokeObjectURL(u));
      setNewBanner({ images: [], imagePreviews: [] });
      setDialogOpen(false); fetchBanners(pagination.page);
    } catch { toast.error("Failed to create banner"); }
    finally { setLoading(false); }
  };

  const updateBanner = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("replaceImages", "true");
      editBanner.images.forEach((img, idx) => {
        if (img instanceof File) { fd.append("images", img); fd.append("imageIndex", idx.toString()); }
        else fd.append("existingImages", JSON.stringify({ index: idx, url: img }));
      });
      await axios.put(`${BASE_URI}/entities/${editBanner.id}?category=banner`, fd, { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${localStorage.getItem("token")}` } });
      toast.success("Banner updated!");
      setEditBanner(null); setDialogOpen(false); fetchBanners(pagination.page);
    } catch { toast.error("Failed to update banner"); }
    finally { setLoading(false); }
  };

  const deleteBanner = async () => {
    try {
      setDeleteLoading(true);
      await axios.delete(`${BASE_URI}/entities/${deleteId}?category=banner`, getAuthHeaders());
      toast.success("Deleted successfully!");
      setDeleteId(null);
      fetchBanners(banners.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page);
    } catch { toast.error("Failed to delete."); }
    finally { setDeleteLoading(false); }
  };

  const openAdd = () => { setNewBanner({ images: [], imagePreviews: [] }); setEditBanner(null); setDialogOpen(true); };
  const openEdit = (b) => { setEditBanner({ ...b, imagePreviews: b.images.map((u) => (typeof u === "string" ? u : URL.createObjectURL(u))) }); setDialogOpen(true); };

  const handleDialogClose = (o) => {
    if (!o) {
      setEditBanner(null);
      setNewBanner({ images: [], imagePreviews: [] });
      setCropModalOpen(false);
      setCropImageUrl("");
      setPendingFiles([]);
    }
    setDialogOpen(o);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Banner Management"
        description="Manage homepage hero banners with image cropping support"
        action={<Button onClick={openAdd}><Plus className="h-4 w-4" /> Add Banner</Button>}
      />
      <InfoBanner
        title="Banner Management"
        description="Upload and crop hero banner images. Images are compressed automatically after cropping."
        capabilities={["Built-in image cropper", "Multiple images per banner", "Auto JPEG compression"]}
      />
      {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Banner ID</TableHead>
              <TableHead>Images</TableHead>
              <TableHead>Count</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-12 text-gray-400">
                <div className="flex items-center justify-center gap-2"><div className="h-4 w-4 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />Loading…</div>
              </TableCell></TableRow>
            ) : banners.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-12 text-gray-400">No banners yet.</TableCell></TableRow>
            ) : banners.map((b) => (
              <TableRow key={b.id}>
                <TableCell className="font-medium text-gray-900">#{b.id}</TableCell>
                <TableCell>
                  <div className="flex gap-2 flex-wrap">
                    {(b.images || []).slice(0, 4).map((url, i) => (
                      <img key={i} src={url} alt={`Banner ${b.id} - ${i + 1}`} className="h-12 w-20 rounded-lg object-cover border border-gray-100" onError={(e) => { e.target.style.display = "none"; }} />
                    ))}
                    {(b.images || []).length > 4 && <span className="text-xs text-gray-400 self-center">+{b.images.length - 4} more</span>}
                  </div>
                </TableCell>
                <TableCell className="text-gray-600">{(b.images || []).length} image{(b.images || []).length !== 1 ? "s" : ""}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(b)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="destructive" onClick={() => setDeleteId(b.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-1 p-4 border-t border-gray-100">
            <Button size="sm" variant="outline" onClick={() => fetchBanners(pagination.page - 1)} disabled={pagination.page === 1 || loading}>Previous</Button>
            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((p) => (
              <Button key={p} size="sm" variant={p === pagination.page ? "default" : "outline"} onClick={() => fetchBanners(p)} disabled={loading}>{p}</Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => fetchBanners(pagination.page + 1)} disabled={pagination.page === pagination.total_pages || loading}>Next</Button>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={!!deleteId}
        title="Delete banner?"
        description="This action cannot be undone."
        onConfirm={deleteBanner}
        onCancel={() => setDeleteId(null)}
        loading={deleteLoading}
      />

      {/* Add / Edit Dialog — also hosts the crop view to avoid Radix pointer-event blocking */}
      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className={cropModalOpen ? "max-w-2xl p-0 overflow-hidden" : "max-w-2xl"}>
          {cropModalOpen ? (
            // Crop view — rendered inside the same Dialog so Radix doesn't block pointer events
            <div className="flex flex-col bg-gray-900" style={{ height: "75vh" }}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
                <button onClick={cancelCrop} className="text-gray-400 hover:text-white flex items-center gap-1 text-sm">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <h3 className="text-white font-semibold text-sm">Crop Image{pendingFiles.length > 0 ? ` (${pendingFiles.length} more after this)` : ""}</h3>
                <button onClick={cancelCrop} className="text-gray-400 hover:text-white"><X className="h-5 w-5" /></button>
              </div>
              <div className="relative flex-1">
                <Cropper
                  image={cropImageUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={16 / 9}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              </div>
              <div className="flex items-center gap-4 px-4 py-3 border-t border-gray-700">
                <label className="text-gray-300 text-xs">Zoom</label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1 accent-blue-500"
                />
                <Button onClick={saveCroppedImage} disabled={loading}>Apply Crop</Button>
              </div>
            </div>
          ) : (
            // Banner form view
            <>
              <DialogHeader><DialogTitle>{editBanner ? `Edit Banner #${editBanner.id}` : "Add New Banner"}</DialogTitle></DialogHeader>
              <div className="px-6 py-4 space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Banner Images</p>
                  <div className="flex flex-wrap gap-3">
                    {(editBanner ? editBanner.imagePreviews || [] : newBanner.imagePreviews).map((url, i) => (
                      <div key={i} className="relative group">
                        <img src={url} alt="" className="h-24 w-36 rounded-xl object-cover border border-gray-200" />
                        <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                          <span className="text-white text-xs font-medium">Replace</span>
                          <input type="file" accept="image/*" onChange={(e) => handleReplaceImage(e, i)} className="hidden" />
                        </label>
                      </div>
                    ))}
                    <label className="flex flex-col items-center justify-center h-24 w-36 rounded-xl border-2 border-dashed border-gray-300 cursor-pointer hover:bg-gray-50 transition-colors">
                      <Upload className="h-5 w-5 text-gray-400 mb-1" />
                      <span className="text-xs text-gray-500">Add image</span>
                      <input type="file" accept="image/*" multiple onChange={handleImagesChange} className="hidden" />
                    </label>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Images will be opened in the crop tool before saving. Max 5MB each.</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={editBanner ? updateBanner : createBanner} disabled={loading}>{loading ? "Saving…" : editBanner ? "Update Banner" : "Create Banner"}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BannerCrud;
